"use client";

import { useMemo, useState } from "react";
import { Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  buildPineStrategy,
  PINE_STRATEGY_TEMPLATES,
  type PineStrategyBuilderInput,
  type PineStrategyTemplateId,
} from "@/lib/pine/strategy-builder";
import { PineCodePreview } from "./pine-code-preview";
import {
  NumberField,
  Toggle,
  compactInputStyles,
  labelStyles,
} from "./form-fields";

// Anonymous client-side Pine generator. Everything runs in the browser:
// the generator never hits a server endpoint, so there is no per-user
// state and nothing to gate. Free for everyone.

export function PineDemoClient() {
  const first = PINE_STRATEGY_TEMPLATES[0];
  const [builder, setBuilder] = useState<PineStrategyBuilderInput>({
    templateId: first.id,
    strategyName: `Delta-X ${first.name}`,
    symbol: "ES",
    timeframe: "5m",
    fastLength: first.defaultFastLength,
    slowLength: first.defaultSlowLength,
    riskReward: first.defaultRiskReward,
    stopLossPct: first.defaultStopLossPct,
    allowLong: true,
    allowShort: true,
    includeAlerts: true,
  });
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");
  const built = useMemo(() => buildPineStrategy(builder), [builder]);
  const activeTemplate =
    PINE_STRATEGY_TEMPLATES.find((t) => t.id === builder.templateId) ?? first;

  function patch(next: Partial<PineStrategyBuilderInput>) {
    setBuilder((current) => ({ ...current, ...next }));
  }

  function selectTemplate(templateId: PineStrategyTemplateId) {
    const t = PINE_STRATEGY_TEMPLATES.find((x) => x.id === templateId) ?? first;
    patch({
      templateId,
      strategyName: `Delta-X ${t.name}`,
      fastLength: t.defaultFastLength,
      slowLength: t.defaultSlowLength,
      riskReward: t.defaultRiskReward,
      stopLossPct: t.defaultStopLossPct,
    });
  }

  async function copyCode() {
    setCopyError("");
    try {
      await navigator.clipboard.writeText(built.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopyError("Copy failed. Select the code manually or download instead.");
    }
  }

  function downloadCode() {
    const ts = new Date().toISOString();
    const header = [
      `// Delta-X demo export - ${ts}`,
      `// Strategy: ${builder.strategyName}`,
      builder.symbol ? `// Symbol: ${builder.symbol}` : null,
      builder.timeframe ? `// Timeframe: ${builder.timeframe}` : null,
      `// Template: ${builder.templateId}`,
      "// Source: visual builder (demo)",
      "",
    ]
      .filter((line): line is string => line !== null)
      .join("\n");
    const blob = new Blob([`${header}${built.code}`], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${builder.strategyName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "delta-x-strategy"}.pine`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="grid gap-6 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-soft)] p-4 sm:p-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <div className="space-y-4">
        <div>
          <label className={labelStyles} htmlFor="demo-template">
            Template
          </label>
          <select
            id="demo-template"
            className={compactInputStyles}
            value={builder.templateId}
            onChange={(e) => selectTemplate(e.target.value as PineStrategyTemplateId)}
          >
            {PINE_STRATEGY_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                [{t.category} - {t.complexity}] {t.name}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-[11px] leading-relaxed text-[var(--fg-fainter)]">
            {activeTemplate.summary}
            <br />
            <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--fg-subtle)]">
              Fit:
            </span>{" "}
            {activeTemplate.compatibility}
          </p>
        </div>

        <div>
          <label className={labelStyles} htmlFor="demo-name">
            Strategy name
          </label>
          <input
            id="demo-name"
            className={compactInputStyles}
            value={builder.strategyName}
            onChange={(e) => patch({ strategyName: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelStyles} htmlFor="demo-symbol">
              Symbol
            </label>
            <input
              id="demo-symbol"
              className={compactInputStyles}
              value={builder.symbol ?? ""}
              onChange={(e) => patch({ symbol: e.target.value })}
            />
          </div>
          <div>
            <label className={labelStyles} htmlFor="demo-timeframe">
              Timeframe
            </label>
            <input
              id="demo-timeframe"
              className={compactInputStyles}
              value={builder.timeframe ?? ""}
              onChange={(e) => patch({ timeframe: e.target.value })}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <NumberField
            id="demo-fast"
            label="Fast"
            value={builder.fastLength}
            min={1}
            max={500}
            onChange={(fastLength) => patch({ fastLength })}
          />
          <NumberField
            id="demo-slow"
            label="Slow"
            value={builder.slowLength}
            min={1}
            max={500}
            onChange={(slowLength) => patch({ slowLength })}
          />
          <NumberField
            id="demo-stop"
            label="Stop %"
            value={builder.stopLossPct}
            min={0.05}
            max={20}
            step={0.05}
            onChange={(stopLossPct) => patch({ stopLossPct })}
          />
          <NumberField
            id="demo-rr"
            label="R:R"
            value={builder.riskReward}
            min={0.25}
            max={10}
            step={0.25}
            onChange={(riskReward) => patch({ riskReward })}
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Toggle
            label="Long"
            checked={builder.allowLong}
            onChange={(allowLong) => patch({ allowLong })}
          />
          <Toggle
            label="Short"
            checked={builder.allowShort}
            onChange={(allowShort) => patch({ allowShort })}
          />
          <Toggle
            label="Alerts"
            checked={builder.includeAlerts}
            onChange={(includeAlerts) => patch({ includeAlerts })}
          />
        </div>

        <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] p-3 text-[11px] leading-relaxed text-[var(--fg-muted)]">
          {built.validationNotice}
        </div>

        <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] p-3 text-[11px] leading-relaxed text-[var(--fg-muted)]">
          Everything here runs in your browser - pick a template, tune the
          parameters, then copy or download paste-ready Pine v5. Nothing is
          sent to or stored on our servers.
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={copyCode}>
            <Copy size={13} />
            {copied ? "Copied" : "Copy Pine"}
          </Button>
          <Button type="button" variant="secondary" onClick={downloadCode}>
            <Download size={13} />
            Download
          </Button>
        </div>
        {copyError ? (
          <div className="text-[11px] text-amber-300">{copyError}</div>
        ) : null}
        <div className="min-h-0 flex-1">
          <PineCodePreview code={built.code} />
        </div>
      </div>
    </div>
  );
}
